import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaCalendarAlt, FaClock, FaMapMarkerAlt, FaTicketAlt, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { formatTime12Hour } from '../../utils/format';
import '../../styles/components/calendar/calendar.css';

const EventCalendar = ({ showFeaturedOnly = false, limit = 6 }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [dayEventPicker, setDayEventPicker] = useState(null);

  const apiUrl = process.env.REACT_APP_API_URL || '/api';

  useEffect(() => {
    fetchEvents();
  }, [showFeaturedOnly, limit]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      let endpoint = `${apiUrl}/calendar`;
      
      if (showFeaturedOnly) {
        endpoint = `${apiUrl}/calendar/featured?limit=${limit}`;
      } else {
        endpoint = `${apiUrl}/calendar/upcoming?limit=${limit}`;
      }

      const response = await axios.get(endpoint);
      setEvents(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError('Failed to load events');
      setLoading(false);
    }
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }

    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    
    return events.filter(event => {
      const eventDate = new Date(event.startDate);
      return eventDate.toDateString() === date.toDateString();
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatTime = (startTime, endTime) => {
    if (startTime === endTime) {
      return formatTime12Hour(startTime);
    }
    return `${formatTime12Hour(startTime)} - ${formatTime12Hour(endTime)}`;
  };

  const navigateMonth = (direction) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + direction);
      return newDate;
    });
  };

  const isToday = (date) => {
    if (!date) return false;
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isPastDate = (date) => {
    if (!date) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const openDayEvents = (dayEvents) => {
    if (dayEvents.length === 1) {
      setSelectedEvent(dayEvents[0]);
      return;
    }
    setDayEventPicker(dayEvents);
  };

  const handleDayKeyDown = (event, dayEvents) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openDayEvents(dayEvents);
    }
  };

  if (loading) {
    return <div className="calendar-loading">Loading events...</div>;
  }

  if (error) {
    return <div className="calendar-error">{error}</div>;
  }

  const days = getDaysInMonth(currentMonth);
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="event-calendar">
      <div className="calendar-header">
        <button 
          className="nav-button" 
          onClick={() => navigateMonth(-1)}
          aria-label="Previous month"
        >
          <FaChevronLeft />
        </button>
        <h3 className="month-year">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </h3>
        <button 
          className="nav-button" 
          onClick={() => navigateMonth(1)}
          aria-label="Next month"
        >
          <FaChevronRight />
        </button>
      </div>

      <div className="calendar-grid">
        {/* Day headers */}
        {dayNames.map(day => (
          <div key={day} className="day-header">
            {day}
          </div>
        ))}

        {/* Calendar days */}
        {days.map((date, index) => {
          const dayEvents = getEventsForDate(date);
          const hasEvents = dayEvents.length > 0;
          
          return (
            <div
              key={index}
              className={`calendar-day ${!date ? 'empty' : ''} ${isToday(date) ? 'today' : ''} ${isPastDate(date) ? 'past' : ''} ${hasEvents ? 'has-events' : ''}`}
              style={hasEvents ? { '--event-color': dayEvents[0].color || 'var(--primary-color)' } : undefined}
              onClick={() => hasEvents && openDayEvents(dayEvents)}
              onKeyDown={(event) => hasEvents && handleDayKeyDown(event, dayEvents)}
              role={hasEvents ? 'button' : undefined}
              tabIndex={hasEvents ? 0 : -1}
              aria-label={
                hasEvents
                  ? `${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })}: ${dayEvents.length} event${dayEvents.length === 1 ? '' : 's'}`
                  : undefined
              }
              title={hasEvents ? dayEvents.map(e => e.title).join(', ') : undefined}
            >
              {date && (
                <>
                  <span className="day-number">{date.getDate()}</span>
                  {hasEvents && (
                    <div className="event-indicators">
                      {dayEvents.slice(0, 3).map((event, eventIndex) => (
                        <div
                          key={eventIndex}
                          className="event-indicator"
                          style={{ backgroundColor: event.color }}
                          title={event.title}
                        />
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="event-indicator more">
                          +{dayEvents.length - 3}
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>

      {/* Upcoming Events List */}
      {events.length > 0 && (
        <div className="upcoming-events">
          <h4>
            {showFeaturedOnly ? 'Featured Events' : 'Upcoming Events'}
          </h4>
          <div className="events-list">
            {events.slice(0, 3).map(event => (
              <div 
                key={event._id} 
                className="event-item"
                onClick={() => setSelectedEvent(event)}
              >
                <div 
                  className="event-color-bar" 
                  style={{ backgroundColor: event.color }}
                />
                <div className="event-details">
                  <h5 className="event-title">{event.title}</h5>
                  <div className="event-meta">
                    <span className="event-date">
                      <FaCalendarAlt /> {formatDate(event.startDate)}
                    </span>
                    <span className="event-time">
                      <FaClock /> {formatTime(event.startTime, event.endTime)}
                    </span>
                    {event.venue && (
                      <span className="event-venue">
                        <FaMapMarkerAlt /> {event.venue}
                      </span>
                    )}
                  </div>
                </div>
                {event.ticketLink && (
                  <a 
                    href={event.ticketLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="ticket-link"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <FaTicketAlt />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {dayEventPicker && (
        <div className="event-modal-overlay" onClick={() => setDayEventPicker(null)}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Events on this day</h3>
              <button
                className="close-button"
                onClick={() => setDayEventPicker(null)}
                aria-label="Close event list"
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <ul className="event-day-picker-list">
                {dayEventPicker.map((event) => (
                  <li key={event._id}>
                    <button
                      type="button"
                      className="event-day-picker-item"
                      onClick={() => {
                        setSelectedEvent(event);
                        setDayEventPicker(null);
                      }}
                    >
                      <span
                        className="event-day-picker-color"
                        style={{ backgroundColor: event.color || 'var(--primary-color)' }}
                        aria-hidden="true"
                      />
                      <span className="event-day-picker-title">{event.title}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Event Modal */}
      {selectedEvent && (
        <div className="event-modal-overlay" onClick={() => setSelectedEvent(null)}>
          <div className="event-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{selectedEvent.title}</h3>
              <button 
                className="close-button"
                onClick={() => setSelectedEvent(null)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              <div className="event-info">
                <div className="info-item">
                  <FaCalendarAlt />
                  <span>{formatDate(selectedEvent.startDate)}</span>
                </div>
                <div className="info-item">
                  <FaClock />
                  <span>{formatTime(selectedEvent.startTime, selectedEvent.endTime)}</span>
                </div>
                {selectedEvent.venue && (
                  <div className="info-item">
                    <FaMapMarkerAlt />
                    <span>{selectedEvent.venue}</span>
                  </div>
                )}
              </div>
              
              {selectedEvent.description && (
                <div className="event-description">
                  <p>{selectedEvent.description}</p>
                </div>
              )}

              {selectedEvent.ticketLink && (
                <div className="modal-actions">
                  <a 
                    href={selectedEvent.ticketLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    <FaTicketAlt /> Get Tickets
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {events.length === 0 && !loading && (
        <div className="no-events">
          <FaCalendarAlt />
          <p>No upcoming events scheduled</p>
        </div>
      )}
    </div>
  );
};

export default EventCalendar;
